import styled from 'styled-components';

import { Breadcrumb } from 'ui/breadcrumb';
import { InputSearch } from 'ui/inputs/inputSearch';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { Select } from 'ui/selectv2/Select';
import { colors, duration, easing, spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useCampaignOptionsState } from './hooks/useCampaignOptionsState';
import { SelectCustomTheme } from './SelectCustomTheme';
import { PanelView } from '../../constants';
import { IntegrationSelects } from './IntegrationSelects';
import { BlocksTable } from './table/BlocksTable';
import { CampaignAndBlockErrorState } from './CampaignAndBlockErrorState';
import { EmptyIntegrationsState } from './EmptyIntegrationsState';
import { usePanelContext } from '../../contexts/Panel';
import { useTranslations } from '../../hooks/useTranslations';
import { useMovableInkIntegrationContext } from '../../contexts/MovableInkIntegrationContext';
import { useMovableInkBlockContext } from '../../contexts/MovableInkBlockContext';

export const testIds = {
  breadcrumb: 'mi-panel-list-breadcrumb',
  inputSearch: 'mi-panel-list-input-search',
  inputSelect: 'mi-panel-list-input-select',
};

const ListViewBody = styled.div`
  display: flex;
  height: auto;
  flex: 1 1 auto;
  flex-direction: column;
  background-color: ${colors.backgroundSecondary};

  &[data-has-content='false'] {
    height: 100%;
  }
`;

const ControlSection = styled.div`
  display: flex;
  height: auto;
  column-gap: ${spacing.half};
  position: sticky;
  background-color: ${colors.backgroundSecondary};
  margin: 0 -${spacing.plusHalf};
  padding: ${spacing.plusHalf};
  top: -${spacing.plusHalf};
  &[data-is-sticky='true'] {
    z-index: 2;
    border-bottom: ${spacing.single} solid ${colors.borderPrimary};
    border-top: ${spacing.single} solid ${colors.borderPrimary};
    transition: all ${duration.short} ${easing.entrance};
  }

  &[data-is-sticky='false'] {
    transition: all ${duration.short} ${easing.entrance};
  }

  &[data-integration-count='1'] {
    margin-top: -${spacing.plusHalf};
  }

  > div {
    flex: 1 1 auto;
    height: fit-content;
  }
`;

const StyledInputSearch = styled(InputSearch)`
  width: ${pxToRem(110)};
  input {
    min-width: 0;
  }
`;

export const PanelViewList = () => {
  const { t } = useTranslations();
  const {
    onInsert,
    panelSearch,
    selectedEditorImageNode,
    setPanelSearch,
    setPanelView,
    isCampaignSelectorsSticky,
  } = usePanelContext();
  const {
    isFetchingCampaigns,
    options,
    selectedOption,
    isDisabled,
    onSelect,
    hasCampaignsError,
    hasCampaigns,
  } = useCampaignOptionsState();
  const { hasBlocksError, hasBlocks } = useMovableInkBlockContext();
  const { integrations, hasIntegrations } = useMovableInkIntegrationContext();

  const hasContent = hasBlocks || hasCampaigns;

  if (!hasIntegrations) {
    return <EmptyIntegrationsState />;
  }

  return (
    <ListViewBody data-has-content={hasContent}>
      {!!onInsert && !!selectedEditorImageNode && (
        <section data-test={testIds.breadcrumb}>
          <Breadcrumb
            isButton
            breadcrumb={{
              content: t('link.imageDetails'),
              onClick: () => setPanelView(PanelView.Detail),
            }}
            variant={PAGE_HEADER_VARIANTS.SECONDARY}
          />
        </section>
      )}
      <IntegrationSelects />
      <ControlSection
        data-is-sticky={isCampaignSelectorsSticky}
        data-integration-count={integrations.length}
      >
        <StyledInputSearch
          data-test={testIds.inputSearch}
          id={testIds.inputSearch}
          handleClear={() => setPanelSearch('')}
          onChange={setPanelSearch}
          placeholder={t('input.search.placeholder')}
          value={panelSearch}
        />
        <Select
          data-test={testIds.inputSelect}
          id="campaignIdSelect"
          onSelect={onSelect}
          customTheme={SelectCustomTheme}
          options={options}
          value={[selectedOption]}
          placeholder={t('select.campaign.placeholder')}
          isLoading={isFetchingCampaigns}
          isDisabled={isDisabled}
          usePortal
        />
      </ControlSection>
      {hasBlocksError || hasCampaignsError ? <CampaignAndBlockErrorState /> : <BlocksTable />}
    </ListViewBody>
  );
};
